import { cn } from "@/lib/utils";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
} from "@repo/ui/components/ui/form";
import { InputField } from "@repo/ui/components/ui/input-field";
import { useTranslations } from "next-intl";
import {
  type ControllerRenderProps,
  type FieldValues,
  type Path,
  type UseFormReturn,
} from "react-hook-form";

type InputProps<T extends FieldValues> = {
  form: UseFormReturn<T, any, T>;
  fieldName: Path<T>;
  fieldLabel?: string;
  fieldPlaceholder?: string;
  fieldDescription?: string | React.ReactNode;
  required?: boolean;
  disabled?: boolean;
  type?: HTMLInputElement["type"];
  validateEarly?: boolean;
  validateBlur?: boolean;
  pattern?: string;
  error?: boolean;
  inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"];
  autoComplete?: string;
  autoCapitalize?: string;
  autoCorrect?: string;
  serializeInput?: (value: any) => any;
  inputClassName?: string;
  className?: string;
  limit?: number;
  trailingIcon?: React.ReactNode;
  onTrailingIconClick?: () => void;
  trailingIconLabel?: string;
  emojiSuggestions?: readonly string[];
  emojiSuggestionsLabel?: string;
};

export const FormInput = <T extends FieldValues>({
  form,
  fieldName,
  fieldLabel,
  fieldDescription,
  fieldPlaceholder,
  required = false,
  disabled = false,
  type = "text",
  validateEarly = false,
  validateBlur = false,
  pattern,
  error = false,
  inputMode,
  autoComplete,
  autoCapitalize,
  autoCorrect,
  serializeInput,
  inputClassName,
  className,
  limit,
  trailingIcon,
  onTrailingIconClick,
  trailingIconLabel,
  emojiSuggestions,
  emojiSuggestionsLabel,
}: InputProps<T>) => {
  const t = useTranslations();
  const hint = typeof fieldDescription === "string" ? fieldDescription : undefined;
  const hasEmojiSuggestions = Boolean(emojiSuggestions?.length);

  return (
    <FormField
      control={form.control}
      name={fieldName}
      render={({ field, fieldState }) => (
        <FormItem className={cn("flex flex-col space-y-0", className)}>
          <FormControl>
            <InputField
              autoCapitalize={autoCapitalize}
              autoComplete={autoComplete}
              autoCorrect={autoCorrect}
              className={inputClassName}
              data-pw={field.name}
              disabled={disabled}
              emojiSuggestions={emojiSuggestions}
              emojiSuggestionsLabel={emojiSuggestionsLabel}
              error={
                fieldState.error?.message
                  ? t(fieldState.error.message)
                  : undefined
              }
              hint={hint}
              invalid={error}
              inputMode={inputMode}
              label={fieldLabel ?? ""}
              name={field.name}
              onBlur={() => {
                field.onBlur();
                validateBlur && form.trigger(fieldName);
              }}
              onChange={(event) => {
                const value = serializeInput
                  ? serializeInput(event.target.value)
                  : event;

                if (type === "number" && event.target.value === "") {
                  field.onChange(null);
                } else {
                  field.onChange(value);
                }

                validateEarly ||
                  (form.getFieldState(fieldName).error &&
                    form.trigger(fieldName));
              }}
              onEmojiSelect={
                hasEmojiSuggestions
                  ? (emoji) => field.onChange(`${field.value ?? ""}${emoji}`)
                  : undefined
              }
              onTrailingIconClick={onTrailingIconClick}
              pattern={pattern}
              placeholder={fieldPlaceholder}
              ref={field.ref}
              required={required}
              trailingIcon={trailingIcon}
              trailingIconLabel={trailingIconLabel}
              type={type}
              value={field.value ?? ""}
            />
          </FormControl>
          {limit && (
            <span
              className={cn(
                "self-end overflow-hidden text-muted-foreground text-xs transition-all duration-150",
                {
                  "text-destructive": field.value?.length > limit,
                  "h-0 opacity-0":
                    (field.value?.length ?? 0) < limit - limit * 0.2,
                  "mt-1 h-5 opacity-100":
                    (field.value?.length ?? 0) >= limit - limit * 0.2,
                },
              )}
            >
              {field.value?.length} / {limit}
            </span>
          )}
          {fieldDescription && typeof fieldDescription !== "string" && (
            <FormDescription className="pt-2">{fieldDescription}</FormDescription>
          )}
        </FormItem>
      )}
    />
  );
};
